import * as SelectPrimitive from '@radix-ui/react-select';
import { cva } from 'class-variance-authority';
import { ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/slw/shared/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/slw/shared/ui/select';

export interface CustomSelectProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, 'onChange' | 'value'> {
  options: { value: string; label: string }[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  contentClass?: string;
  disabled?: boolean;
  color?: 'white' | 'grey';
  size?: 'sm' | 'md' | 'lg';
}

export const CustomSelect = React.forwardRef<HTMLButtonElement, CustomSelectProps>(
  (
    {
      options,
      placeholder = 'Select...',
      value,
      onChange,
      className,
      contentClass,
      disabled,
      color = 'white',
      size = 'md',
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [selected, setSelected] = React.useState(value || '');

    const handleValueChange = (newValue: string) => {
      setSelected(newValue);
      onChange?.(newValue);
    };

    const selectVariants = cva(
      [
        'w-full min-w-50 rounded-md border p-6 text-base',
        'focus:outline-none focus:ring-0 relative',
        '[&>svg]:hidden',
        'hover:border-default',
        'transition-all duration-200 ease-in-out',
      ],
      {
        variants: {
          size: {
            sm: 'h-10',
            md: 'h-12',
            lg: 'h-16',
          },
          color: {
            white: 'bg-secondary hover:bg-gray-input',
            grey: 'bg-gray-input hover:bg-secondary',
          },
        },
        defaultVariants: {
          size: 'md',
          color: 'white',
        },
      },
    );

    const selectItemVariants = cva(
      'cursor-pointer px-6 flex items-center text-base transition-colors duration-200 ease-in-out',
      {
        variants: {
          size: {
            sm: 'h-10',
            md: 'h-12',
            lg: 'h-16',
          },
          color: {
            white: 'bg-secondary hover:bg-gray-input',
            grey: 'bg-gray-input hover:bg-secondary',
          },
        },
        defaultVariants: {
          size: 'md',
          color: 'white',
        },
      },
    );

    return (
      <Select value={selected} onValueChange={handleValueChange} onOpenChange={setOpen} disabled={disabled}>
        <SelectTrigger
          ref={ref}
          className={cn(
            selectVariants({ size, color }),
            open ? 'border-[#140C07]' : 'border-[#e6e6e6]',
            selected ? 'text-foreground' : 'text-muted-foreground',
            className,
            'p-4',
          )}
          {...props}
        >
          <SelectValue placeholder={placeholder} />
          <div className='relative ml-2 top-1/2 -translate-y-1/2'>
            {open ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
          </div>
        </SelectTrigger>
        <SelectContent className={cn('border-[#e6e6e6] bg-white p-0', contentClass)}>
          <div className='max-h-[--radix-select-content-available-height] overflow-y-auto'>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} className={selectItemVariants({ size, color })}>
                {option.label}
              </SelectItem>
            ))}
          </div>
        </SelectContent>
      </Select>
    );
  },
);

CustomSelect.displayName = 'CustomSelect';
